import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, Input, Output, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i4 from '@appkit4/angular-components/field';
import { FieldModule } from '@appkit4/angular-components/field';
import * as i1 from '@appkit4/angular-api';

class PaginationComponent {
    constructor(localizationConfig) {
        this.localizationConfig = localizationConfig;
        this.max = 100;
        this.min = 1;
        this.step = 1;
        this.value = 1;
        this.onChange = new EventEmitter();
        this.inputFocused = false;
        this.onModelChange = (_) => { };
        this.onTouched = () => { };
    }
    registerOnChange(fn) {
        this.onModelChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        this.value = value;
    }
    ngOnInit() {
        this.onValueChange({
            target: {
                value: 1,
            },
        });
    }
    onPressEnter(event) {
        event.target.blur();
    }
    onPressUp(event) {
        event.preventDefault();
        const value = this.checkBoundary(this.value + this.step);
        if (value > this.value) {
            this.value = value;
            this.onPageIndexChange(value);
        }
        this.value = value;
        this.onModelChange(this.value);
    }
    onPressDown(event) {
        event.preventDefault();
        const value = this.checkBoundary(this.value - this.step);
        if (value < this.value) {
            this.value = value;
            this.onPageIndexChange(value);
        }
        this.value = value;
        this.onModelChange(this.value);
    }
    onPageIndexChange(index) {
        this.onChange.next(index);
    }
    onInputFocus(event) {
        this.inputFocused = true;
    }
    onInputBlur(event) {
        this.inputFocused = false;
        event.target.value = this.checkBoundary(event.target.value);
    }
    prev() {
        const value = this.checkBoundary(this.value - this.step);
        if (value < this.value) {
            this.value = value;
            this.onPageIndexChange(value);
        }
        this.value = value;
        this.onModelChange(this.value);
    }
    next() {
        const value = this.checkBoundary(this.value + this.step);
        if (value > this.value) {
            this.value = value;
            this.onPageIndexChange(value);
        }
        this.value = value;
        this.onModelChange(this.value);
    }
    checkBoundary(value) {
        if (value < this.min)
            value = this.min;
        if (value > this.max)
            value = this.max;
        return isNaN(value) || value === ""
            ? this.checkBoundary(1)
            : Math.round(value);
    }
    onValueChange(event) {
        const modelToPropagate = this.checkBoundary(event.target.value);
        this.onModelChange(modelToPropagate);
        this.onTouched();
        this.writeValue(modelToPropagate);
        this.onPageIndexChange(modelToPropagate);
    }
    getAriaLabel(labelKey) {
        return this.localizationConfig.translation ? this.localizationConfig.translation[labelKey] : null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: PaginationComponent, deps: [{ token: i1.LocalizationConfig }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.11", type: PaginationComponent, selector: "ap-pagination", inputs: { max: "max", min: "min", label: "label", step: "step", style: "style", styleClass: "styleClass" }, outputs: { onChange: "onChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => PaginationComponent),
                multi: true,
            },
        ], ngImport: i0, template: `
    <div class="ap-pagination {{ styleClass }}" [style]="style">
      <div
        class="ap-pagination-btn-prev"
        [class.large]="label"
        [class.disabled]="value <= min"
        (click)="prev()"
        (keydown.enter)="prev()"
        [attr.tabindex]="value <= min && !inputFocused ? undefined : '0'"
        [attr.aria-label]="getAriaLabel('previous')"
        role="button"
        [attr.aria-disabled]="value <= min"
      >
        <span
          class="Appkit4-icon icon-left-chevron-outline height ap-font-medium"
        ></span>
        <span class="ap-pagination-btn-text" *ngIf="label">{{ label[0] }}</span>
      </div>
      <div class="ap-pagination-field">
        <ap-field [hideTitleOnInput]="true">
          <input appkit-field [(ngModel)]="value"
            (focus)="onInputFocus($event)"
            (blur)="onInputBlur($event)"
            (change)="onValueChange($event)"
            (keyup.enter)="onPressEnter($event)"
            (keydown.arrowup)="onPressUp($event)"
            (keydown.arrowdown)="onPressDown($event)" />
        </ap-field>
        <span class="ap-pagination-total">
          <span>{{getAriaLabel('of')}}</span>
          <span>{{max}}</span>
        </span>
      </div>
      <div
        class="ap-pagination-btn-next"
        [class.large]="label"
        [class.disabled]="value >= max"
        (click)="next()"
        (keydown.enter)="next()"
        [attr.tabindex]="value >= max && !inputFocused ? undefined : '0'"
        [attr.aria-label]="getAriaLabel('next')"
        role="button"
        [attr.aria-disabled]="value >= max"
      >
        <span class="ap-pagination-btn-text" *ngIf="label">{{ label[1] }}</span>
        <span
          class="icon-right-chevron-outline Appkit4-icon height ap-font-medium"
        ></span>
      </div>
    </div>
  `, isInline: true, styles: [":host{display:flex}:host .ap-pagination{display:grid;grid-auto-flow:column;padding:var(--spacing-2);height:3.5rem;border-radius:var(--border-radius-3)}:host .ap-pagination .ap-pagination-btn-prev,:host .ap-pagination .ap-pagination-btn-next{margin:var(--spacing-2);line-height:2.5rem;border-radius:var(--border-radius-2);text-align:center;color:var(--color-text-body);cursor:pointer;-webkit-user-select:none;user-select:none}:host .ap-pagination .ap-pagination-btn-prev .Appkit4-icon,:host .ap-pagination .ap-pagination-btn-next .Appkit4-icon{font-weight:var(--font-weight-2)}:host .ap-pagination .ap-pagination-btn-prev .Appkit4-icon:before,:host .ap-pagination .ap-pagination-btn-next .Appkit4-icon:before{font-weight:var(--font-weight-2)}:host .ap-pagination .ap-pagination-btn-prev .height,:host .ap-pagination .ap-pagination-btn-next .height{height:1.5rem}:host .ap-pagination .ap-pagination-btn-prev:before,:host .ap-pagination .ap-pagination-btn-next:before{width:2.125rem;height:2.125rem;line-height:2.125rem;font-size:1rem}:host .ap-pagination .ap-pagination-btn-prev:not(.disabled):hover,:host .ap-pagination .ap-pagination-btn-next:not(.disabled):hover{background-color:var(--color-background-hover)}:host .ap-pagination .ap-pagination-btn-prev.disabled,:host .ap-pagination .ap-pagination-btn-next.disabled{cursor:not-allowed;color:var(--color-text-disabled)}:host .ap-pagination .ap-pagination-btn-prev:not(.large){width:2.5rem}:host .ap-pagination .ap-pagination-btn-next:not(.large){width:2.5rem}:host .ap-pagination .ap-pagination-btn-prev.large{padding:0 var(--spacing-7) 0 var(--spacing-4)}:host .ap-pagination .ap-pagination-btn-prev.large .ap-pagination-btn-text{margin-left:var(--spacing-2);line-height:2rem;height:1.5rem;display:inline-block}:host .ap-pagination .ap-pagination-btn-next.large{padding:0 var(--spacing-4) 0 var(--spacing-7);height:2.5rem;text-align:left}:host .ap-pagination .ap-pagination-btn-next.large .ap-pagination-btn-text{margin-right:var(--spacing-2);line-height:2rem;height:1.5rem;display:inline-block}:host .ap-pagination .ap-pagination-field{display:flex;column-gap:var(--spacing-3);align-items:center;padding:var(--spacing-2);margin:var(--spacing-2) 0 var(--spacing-2) 0;min-width:6.75rem}:host .ap-pagination .ap-pagination-field ::ng-deep .ap-field{width:3rem}:host .ap-pagination .ap-pagination-field ::ng-deep .ap-field .ap-field-wrapper{height:2rem}:host .ap-pagination .ap-pagination-field ::ng-deep .ap-field .ap-field-wrapper input{margin:var(--spacing-2) var(--spacing-3);text-align:center}:host .ap-pagination .ap-pagination-field .ap-pagination-total{display:flex;column-gap:var(--spacing-2);color:var(--color-text-body)}:host .ap-pagination .ap-pagination-field span{pointer-events:none;-webkit-user-select:none;user-select:none;line-height:1.5rem}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i4.FieldComponent, selector: "ap-field", inputs: ["styleClass", "style", "title", "error", "disabled", "selectedItem", "customImg", "img", "unit", "type", "required", "readonly", "revealer", "hideTitleOnInput", "textReadonly", "dropdown", "fieldId", "attachedDOM", "adaptivePosition", "minNum", "maxNum", "ariaLabel", "defaultNum", "format", "datepicker", "editable", "useCustomValidation", "disableOutsideClose", "dpLanguage", "dpRange", "dpAutoClose", "dpCalendarPosition", "dpType", "dpCustom", "dpOptions", "dpMinDate", "dpMaxDate", "dpDisabledDates", "dpDisabledRanges", "dpDisabledDays", "dpDateformat", "dpDateSeparator", "dpShowOtherMonths", "dpRangeConnecterFormat", "dpRangeConnectorFormat", "dpFirstDayOfWeek", "dpView", "dpSelectedDates"], outputs: ["onNumberChange", "onKeydown", "onClick", "onClose", "onShow", "onClickOutside", "onListItemFocus", "dpSelectedDatesChange", "dpOnDateInvalid", "dpOnClickNavigators"] }, { kind: "directive", type: i4.FieldDirective, selector: "[appkit-field]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: "ap-pagination", template: `
    <div class="ap-pagination {{ styleClass }}" [style]="style">
      <div
        class="ap-pagination-btn-prev"
        [class.large]="label"
        [class.disabled]="value <= min"
        (click)="prev()"
        (keydown.enter)="prev()"
        [attr.tabindex]="value <= min && !inputFocused ? undefined : '0'"
        [attr.aria-label]="getAriaLabel('previous')"
        role="button"
        [attr.aria-disabled]="value <= min"
      >
        <span
          class="Appkit4-icon icon-left-chevron-outline height ap-font-medium"
        ></span>
        <span class="ap-pagination-btn-text" *ngIf="label">{{ label[0] }}</span>
      </div>
      <div class="ap-pagination-field">
        <ap-field [hideTitleOnInput]="true">
          <input appkit-field [(ngModel)]="value"
            (focus)="onInputFocus($event)"
            (blur)="onInputBlur($event)"
            (change)="onValueChange($event)"
            (keyup.enter)="onPressEnter($event)"
            (keydown.arrowup)="onPressUp($event)"
            (keydown.arrowdown)="onPressDown($event)" />
        </ap-field>
        <span class="ap-pagination-total">
          <span>{{getAriaLabel('of')}}</span>
          <span>{{max}}</span>
        </span>
      </div>
      <div
        class="ap-pagination-btn-next"
        [class.large]="label"
        [class.disabled]="value >= max"
        (click)="next()"
        (keydown.enter)="next()"
        [attr.tabindex]="value >= max && !inputFocused ? undefined : '0'"
        [attr.aria-label]="getAriaLabel('next')"
        role="button"
        [attr.aria-disabled]="value >= max"
      >
        <span class="ap-pagination-btn-text" *ngIf="label">{{ label[1] }}</span>
        <span
          class="icon-right-chevron-outline Appkit4-icon height ap-font-medium"
        ></span>
      </div>
    </div>
  `, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => PaginationComponent),
                            multi: true,
                        },
                    ], styles: [":host{display:flex}:host .ap-pagination{display:grid;grid-auto-flow:column;padding:var(--spacing-2);height:3.5rem;border-radius:var(--border-radius-3)}:host .ap-pagination .ap-pagination-btn-prev,:host .ap-pagination .ap-pagination-btn-next{margin:var(--spacing-2);line-height:2.5rem;border-radius:var(--border-radius-2);text-align:center;color:var(--color-text-body);cursor:pointer;-webkit-user-select:none;user-select:none}:host .ap-pagination .ap-pagination-btn-prev .Appkit4-icon,:host .ap-pagination .ap-pagination-btn-next .Appkit4-icon{font-weight:var(--font-weight-2)}:host .ap-pagination .ap-pagination-btn-prev .Appkit4-icon:before,:host .ap-pagination .ap-pagination-btn-next .Appkit4-icon:before{font-weight:var(--font-weight-2)}:host .ap-pagination .ap-pagination-btn-prev .height,:host .ap-pagination .ap-pagination-btn-next .height{height:1.5rem}:host .ap-pagination .ap-pagination-btn-prev:before,:host .ap-pagination .ap-pagination-btn-next:before{width:2.125rem;height:2.125rem;line-height:2.125rem;font-size:1rem}:host .ap-pagination .ap-pagination-btn-prev:not(.disabled):hover,:host .ap-pagination .ap-pagination-btn-next:not(.disabled):hover{background-color:var(--color-background-hover)}:host .ap-pagination .ap-pagination-btn-prev.disabled,:host .ap-pagination .ap-pagination-btn-next.disabled{cursor:not-allowed;color:var(--color-text-disabled)}:host .ap-pagination .ap-pagination-btn-prev:not(.large){width:2.5rem}:host .ap-pagination .ap-pagination-btn-next:not(.large){width:2.5rem}:host .ap-pagination .ap-pagination-btn-prev.large{padding:0 var(--spacing-7) 0 var(--spacing-4)}:host .ap-pagination .ap-pagination-btn-prev.large .ap-pagination-btn-text{margin-left:var(--spacing-2);line-height:2rem;height:1.5rem;display:inline-block}:host .ap-pagination .ap-pagination-btn-next.large{padding:0 var(--spacing-4) 0 var(--spacing-7);height:2.5rem;text-align:left}:host .ap-pagination .ap-pagination-btn-next.large .ap-pagination-btn-text{margin-right:var(--spacing-2);line-height:2rem;height:1.5rem;display:inline-block}:host .ap-pagination .ap-pagination-field{display:flex;column-gap:var(--spacing-3);align-items:center;padding:var(--spacing-2);margin:var(--spacing-2) 0 var(--spacing-2) 0;min-width:6.75rem}:host .ap-pagination .ap-pagination-field ::ng-deep .ap-field{width:3rem}:host .ap-pagination .ap-pagination-field ::ng-deep .ap-field .ap-field-wrapper{height:2rem}:host .ap-pagination .ap-pagination-field ::ng-deep .ap-field .ap-field-wrapper input{margin:var(--spacing-2) var(--spacing-3);text-align:center}:host .ap-pagination .ap-pagination-field .ap-pagination-total{display:flex;column-gap:var(--spacing-2);color:var(--color-text-body)}:host .ap-pagination .ap-pagination-field span{pointer-events:none;-webkit-user-select:none;user-select:none;line-height:1.5rem}\n"] }]
        }], ctorParameters: () => [{ type: i1.LocalizationConfig }], propDecorators: { max: [{
                type: Input
            }], min: [{
                type: Input
            }], label: [{
                type: Input
            }], step: [{
                type: Input
            }], style: [{
                type: Input
            }], styleClass: [{
                type: Input
            }], onChange: [{
                type: Output
            }] } });
class PaginationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: PaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.11", ngImport: i0, type: PaginationModule, declarations: [PaginationComponent], imports: [CommonModule, FormsModule, FieldModule], exports: [PaginationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: PaginationModule, imports: [CommonModule, FormsModule, FieldModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: PaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, FieldModule],
                    declarations: [PaginationComponent],
                    exports: [PaginationComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PaginationComponent, PaginationModule };
